import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3142District, U3142Cadastre } from "../../U3142Service";
import {
  parseConcordanceEntry,
  buildFullConcordanceEntry,
  validateConcordanceEntries,
  type U3141ParsedConcordanceEntry,
} from "../../../u3141LandRegister/components/form/u3141ChildLotChain/U3141ConcordanceUtils";
import {
  useU3142FindDistrictsHandlers,
  useU3142FindCadastresHandlers,
  useU3142ResolveConcordanceHandlers,
} from "./U3142UseFindDistrictCadastreHandlers";
import { NS } from "../../U3142";
import { FaCheck, FaPlus, FaTimesCircle, FaTrash } from "react-icons/fa";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { setNestedValue } from "../../../u2000Core/tools/U2000ObjectUtils";

export interface U3142ConcordanceRow {
  lot: string;
  codeDistrict?: string;
  cadastreCode?: string;
  rankCode?: string;
  rankLabel?: string;
  district?: U3142District;
  cadastre?: U3142Cadastre;
}

export interface U3142ConcordanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  concordance: string[];
  onSave: (concordance: string[]) => void;
}

function rowFromParsed(p: U3141ParsedConcordanceEntry): U3142ConcordanceRow {
  return {
    lot: p.lot,
    codeDistrict: p.codeDistrict,
    cadastreCode: p.cadastreCode,
    rankCode: p.rankCode,
    rankLabel: p.rankLabel,
    district: undefined,
    cadastre: undefined,
  };
}

function buildConcordanceStrings(rows: U3142ConcordanceRow[]): string[] {
  return rows
    .filter((r) => (r.lot ?? "").trim() !== "")
    .map((row) => {
      const codeDistrict = row.codeDistrict ?? row.district?.codeDistrict ?? "";
      const cadastreCode = row.cadastreCode ?? row.cadastre?.cadastreCode ?? "";
      const rankCode = row.rankCode ?? row.cadastre?.rankCode ?? "";
      const rankLabel = row.rankLabel ?? row.cadastre?.rankLabel ?? "";
      const propertyDistrict = row.district?.propertyDistrict ?? "";
      const cadastre = row.cadastre?.cadastre ?? "";
      if (codeDistrict && cadastreCode && rankCode && rankLabel) {
        return buildFullConcordanceEntry(row.lot.trim(), codeDistrict, cadastreCode, rankCode, propertyDistrict, cadastre, rankLabel);
      }
      return row.lot.trim();
    });
}

interface ConcordanceRowEditorProps {
  index: number;
  formStateHandlers: any;
  findDistrictsHandlers: ReturnType<typeof useU3142FindDistrictsHandlers>;
  onDelete: () => void;
  canDelete: boolean;
}

function ConcordanceRowEditor({
  index,
  formStateHandlers,
  findDistrictsHandlers,
  onDelete,
  canDelete,
}: ConcordanceRowEditorProps) {
  const { t } = useTranslation();
  const baseField = `rows.${index}`;
  const getDistrictId = useCallback(() => {
    const data = formStateHandlers.getData();
    const row = data?.rows?.[index];
    return row?.district?.id;
  }, [formStateHandlers, index]);

  const findCadastresForRow = useU3142FindCadastresHandlers({ getDistrictId });

  const onSearchDistrict = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      return findDistrictsHandlers.search(criterias);
    },
    [findDistrictsHandlers]
  );

  const onSearchCadastre = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      if (!getDistrictId()) return [];
      const results = await findCadastresForRow.search(criterias);
      return results.results.map((r: Record<string, unknown>) => ({ value: r.id, ...r }));
    },
    [findCadastresForRow, getDistrictId]
  );

  const extractDistrict = useCallback(
    (result: any) => {
      if (result) {
        return [
          { fieldName: `${baseField}.district`, value: result },
          { fieldName: `${baseField}.codeDistrict`, value: result.codeDistrict ?? null },
        ];
      }
      return [
        { fieldName: `${baseField}.district`, value: null },
        { fieldName: `${baseField}.codeDistrict`, value: null },
        { fieldName: `${baseField}.cadastre`, value: null },
        { fieldName: `${baseField}.cadastreCode`, value: null },
        { fieldName: `${baseField}.rankCode`, value: null },
        { fieldName: `${baseField}.rankLabel`, value: null },
      ];
    },
    [baseField]
  );

  const extractCadastre = useCallback(
    (result: any) => {
      if (result) {
        return [
          { fieldName: `${baseField}.cadastre`, value: result },
          { fieldName: `${baseField}.cadastreCode`, value: result.cadastreCode ?? null },
          { fieldName: `${baseField}.rankCode`, value: result.rankCode ?? null },
          { fieldName: `${baseField}.rankLabel`, value: result.rankLabel ?? null },
        ];
      }
      return [
        { fieldName: `${baseField}.cadastre`, value: null },
        { fieldName: `${baseField}.cadastreCode`, value: null },
        { fieldName: `${baseField}.rankCode`, value: null },
        { fieldName: `${baseField}.rankLabel`, value: null },
      ];
    },
    [baseField]
  );

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      <td className="px-3 py-2 align-top w-[18%]">
        <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${baseField}.lot`} label="" readonly={false} required={false} />
      </td>
      <td className="px-3 py-2 align-top w-[32%]">
        <U2000AutoCompleteInput
          pageSize={15}
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.district`}
          mainFieldName="propertyDistrict"
          label=""
          onSearch={onSearchDistrict}
          displayFn={(item: any) => (item ? `${item.propertyDistrict}` : "")}
          overrideExtractDataFn={extractDistrict}
          basicResultHeight={300}
          resultLabels={[]}
          resultFields={["propertyDistrict"]}
        />
      </td>
      <td className="px-3 py-2 align-top w-[32%]">
        <U2000AutoCompleteInput
          pageSize={15}
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.cadastre`}
          mainFieldName="cadastre"
          label=""
          onSearch={onSearchCadastre}
          displayFn={(item: any) => (item ? `${item.cadastre} ${item.rankLabel ?? ""}` : "")}
          overrideExtractDataFn={extractCadastre}
          basicResultHeight={300}
          minCarLaunchSearch={2}
          resultLabels={[]}
          resultFields={["cadastre", "rankLabel"]}
          readonly={!getDistrictId()}
        />
      </td>
      <td className="px-3 py-2 align-top w-[18%] text-right">
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-red-500"
            aria-label={t("btnDelete", { ns: NS })}
            title={t("btnDelete", { ns: NS })}
          >
            <FaTrash className="w-4 h-4" />
            <span className="sr-only">{t("btnDelete", { ns: NS })}</span>
          </button>
        )}
      </td>
    </tr>
  );
}

export default function U3142ConcordanceDialog({ isOpen, onClose, concordance, onSave }: U3142ConcordanceDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [data, setData] = useState<{ rows: U3142ConcordanceRow[] }>({ rows: [] });
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const findDistrictsHandlers = useU3142FindDistrictsHandlers();
  const { resolveDistrict, resolveCadastre } = useU3142ResolveConcordanceHandlers();

  useEffect(() => {
    if (!isOpen) return;
    const rows: U3142ConcordanceRow[] = (concordance ?? []).map((entry) => rowFromParsed(parseConcordanceEntry(entry)));
    const initialRows = rows.length > 0 ? rows : [{ lot: "", district: undefined, cadastre: undefined }];
    setData({ rows: initialRows });

    (async () => {
      const resolved = await Promise.all(
        initialRows.map(async (row): Promise<U3142ConcordanceRow> => {
          const hasDistrictId = row.district?.id != null && String(row.district.id).trim() !== "";
          const hasCadastreId = row.cadastre?.id != null && String(row.cadastre.id).trim() !== "";
          let district = row.district;
          if (!hasDistrictId && (row.codeDistrict ?? row.district?.propertyDistrict)) {
            const resolvedDistrict = await resolveDistrict(row.codeDistrict ?? undefined, row.district?.propertyDistrict ?? undefined);
            if (resolvedDistrict) district = resolvedDistrict;
          }
          if (!hasCadastreId && district?.id && (row.cadastreCode ?? row.cadastre?.cadastre ?? row.cadastre?.rankLabel)) {
            const resolvedCadastre = await resolveCadastre(
              String(district.id),
              row.cadastreCode ?? row.cadastre?.cadastreCode,
              row.cadastre?.cadastre ?? row.cadastre?.rankLabel ?? row.rankLabel,
              row.rankCode ?? row.cadastre?.rankCode,
              row.rankLabel ?? row.cadastre?.rankLabel,
            );
            if (resolvedCadastre) {
              return { ...row, district, cadastre: resolvedCadastre };
            }
          }
          return { ...row, district };
        }),
      );
      setData({ rows: resolved });
    })();
    // resolveDistrict/resolveCadastre exclus des deps pour ne pas réinitialiser à chaque render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, concordance]);

  const formStateHandlers = useMemo(() => {
    const getData = () => data;
    const setDataState = (upd: any) => setData((prev) => ({ ...prev, ...upd }));
    const onChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string | number | boolean | any) => {
      const target = _e?.target as HTMLInputElement | undefined;
      const fieldName = target?.id ?? target?.name;
      if (!fieldName) return;
      const current = getData();
      const updated = JSON.parse(JSON.stringify(current ?? {}));
      setNestedValue(updated, fieldName, value);
      setDataState(updated);
    };
    return {
      getData,
      setData: setDataState,
      getTouched: () => ({}),
      setTouched: () => {},
      getErrors: () => ({}),
      setErrors: () => {},
      getFormState: () => U2000FormStateEnumState.EDIT,
      isSubmitted: () => false,
      getOtherErrors: () => undefined,
      setOtherErrors: () => {},
      onChange,
    };
  }, [data]);

  const handleAddRow = useCallback(() => {
    setData((prev) => ({
      ...prev,
      rows: [...prev.rows, { lot: "", district: undefined, cadastre: undefined }],
    }));
  }, []);

  const handleDeleteRow = useCallback((index: number) => {
    setDeleteIndex(index);
  }, []);

  const confirmDeleteRow = useCallback(() => {
    if (deleteIndex === null) return;
    setData((prev) => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== deleteIndex),
    }));
    setDeleteIndex(null);
  }, [deleteIndex]);

  const handleClose = useCallback(
    (action: "save" | "cancel") => {
      if (action === "save") {
        const strings = buildConcordanceStrings(data.rows);
        const validation = validateConcordanceEntries(strings);
        if (!validation.valid && validation.errors?.length) {
          validation.errors.forEach((err) => toast.error(err, t("lblConcordance", { ns: NS })));
          return;
        }
        onSave(strings);
        /* Pas de toast : rien n’est envoyé au serveur ici, la persistance a lieu au save du lot. */
      }
      onClose();
    },
    [data.rows, onSave, onClose, toast, t]
  );

  const isValid = true;
  const emptyMessage = data.rows.length === 0 || (data.rows.length === 1 && !data.rows[0].lot?.trim());

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t("lblConcordanceDialogTitle", { ns: NS, defaultValue: "Modifier les concordances" })}
      width="large"
      height="max-h-[85vh]"
      isValid={isValid}
    >
      <div className="relative flex flex-col gap-4">
        {deleteIndex !== null && (
          <div
            className="absolute inset-0 z-100 flex items-center justify-center rounded-lg bg-black/30 backdrop-blur-[1px]"
            aria-modal="true"
            role="alertdialog"
            aria-labelledby="confirm-delete-title"
          >
            <div className="relative z-101 mx-4 w-full max-w-md rounded-xl border border-gray-200 bg-surface p-5 shadow-lg">
              <h3 id="confirm-delete-title" className="text-lg font-semibold text-gray-900">
                {t("dlgConfirmDeleteConcordanceTitle", { ns: NS, defaultValue: "Supprimer cette concordance ?" })}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {t("dlgConfirmDeleteConcordanceMessage", { ns: NS, defaultValue: "Voulez-vous supprimer cette entrée de la liste ?" })}
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteIndex(null)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaTimesCircle className="h-4 w-4" />
                  {t("dlgLblNo", { defaultValue: "Non" })}
                </button>
                <button
                  type="button"
                  onClick={() => confirmDeleteRow()}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  <FaCheck className="h-4 w-4" />
                  {t("dlgLblYes", { defaultValue: "Oui" })}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 bg-surface shadow-none">
          {emptyMessage && (
            <p className="text-sm text-gray-600">
              {t("lblConcordanceEmptyHint", { ns: NS, defaultValue: "Aucune concordance. Cliquez sur Ajouter pour en ajouter une." })}
            </p>
          )}
          <div className="overflow-x-auto overflow-y-auto max-h-[50vh] rounded-lg border border-gray-200 shadow-none">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-muted border-b border-gray-200 z-10 shadow-none">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-700 w-[18%]">{t("lblLotDetail.lot", { ns: NS })}</th>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-700 w-[32%]">{t("lblLotDetail.propertyDistrict", { ns: NS })}</th>
                  <th className="px-3 py-2.5 text-left font-medium text-gray-700 w-[32%]">{t("lblLotDetail.cadastre", { ns: NS })}</th>
                  <th className="px-3 py-2.5 text-right font-medium text-gray-700 w-[18%]">
                    <button
                      type="button"
                      onClick={handleAddRow}
                      className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      aria-label={t("btnAdd", { ns: NS })}
                    >
                      <FaPlus className="w-4 h-4" />
                      <span>{t("btnAdd", { ns: NS })}</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((_, idx) => (
                  <ConcordanceRowEditor
                    key={idx}
                    index={idx}
                    formStateHandlers={formStateHandlers}
                    findDistrictsHandlers={findDistrictsHandlers}
                    onDelete={() => handleDeleteRow(idx)}
                    canDelete={data.rows.length > 1}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
}
